import { Baggage, SelectedBaggage } from '../../BaggageService/types';
import { BaggageDirection, BaggageItem, BaggageItemCount } from '../BaggageList/types';
import { SelectedAdditionalBaggage } from './types';
import { SelectedUpgrade } from '../IncludedBaggage/types';
import { Segment, SelectedBaggageService } from '../types';
import { Money } from '../../Money/types';
import { CheckoutOrderFragment, SegmentWithTransferFragment } from '@websky/graphql';

export const convertBaggageOnBaggageItem = (
	baggages: Baggage[],
	selectedServices: SelectedAdditionalBaggage[],
	segments: Segment[]
): BaggageItem => {
	const firstBaggage = baggages[0];

	const minPrice: Money = {
		amount: firstBaggage.price?.amount * firstBaggage.segmentIds[0]?.length,
		currency: firstBaggage.price?.currency
	};

	baggages?.forEach(baggage => {
		baggage.segmentIds.forEach(segmentId => {
			if (!minPrice.amount || baggage.price.amount * segmentId.length < minPrice?.amount) {
				minPrice.amount = baggage.price.amount * segmentId.length;
				minPrice.currency = baggage.price.currency;
			}
		});
	});

	return {
		baggage: baggages,
		selected: isSelected(baggages[0], selectedServices),
		priceFrom: minPrice,
		servicesBySegments: getSelectedServicesBySegments(baggages, selectedServices, segments)
	};
};

export const getSelectedServicesBySegments = (
	baggages: Baggage[],
	selectedServices: SelectedAdditionalBaggage[],
	segments: Segment[],
	splitByServiceId?: boolean
) => {
	const totalSegments = segments.length;
	const baggageMap = new Map(baggages.map(baggage => [baggage.id, baggage]));
	const segmentMap = new Map(segments.map(segment => [segment.id, segment]));

	// Collect selected services, merging segmentIds and count
	const selectedServicesByPassengerMap = new Map<
		string,
		{
			serviceIds: string[];
			segmentIds: string[];
			count: number;
			isAllSegments: boolean;
			selectedServices: SelectedBaggageService[];
		}
	>();

	for (const selectedService of selectedServices) {
		const baggageService = baggageMap.get(selectedService.serviceId);

		if (!baggageService) {
			continue;
		}

		const key = `${splitByServiceId ? selectedService.serviceId : baggageService.rfics}/${
			selectedService.passengerId
		}/${selectedService.count}`;

		const existService = selectedServicesByPassengerMap.get(key);

		const serviceIds = new Set([selectedService.serviceId, ...(existService?.serviceIds ?? [])]);
		const segmentIds = new Set([...selectedService.segmentIds, ...(existService?.segmentIds ?? [])]);
		const count = Math.max(selectedService.count, existService?.count || 0);
		const isAllSegments = segmentIds.size === totalSegments;

		selectedServicesByPassengerMap.set(key, {
			count,
			isAllSegments,
			serviceIds: [...serviceIds].sort(),
			segmentIds: [...segmentIds].sort(),
			selectedServices: [...(existService?.selectedServices ?? []), selectedService]
		});
	}

	const selectedServicesMap = new Map<string, BaggageItemCount>();
	for (const [serviceKey, selectedService] of selectedServicesByPassengerMap.entries()) {
		const segmentIds = selectedService.segmentIds;
		const key = `${serviceKey.split('/')[0]}/${segmentIds.join('/')}`;

		const existing = selectedServicesMap.get(key);

		if (existing) {
			existing.count += selectedService.count;
			existing.selectedServices = [...(existing.selectedServices ?? []), ...selectedService.selectedServices];
		} else {
			selectedServicesMap.set(key, {
				count: selectedService.count,
				directions: segmentIds
					.map(id => segmentMap.get(id))
					.filter((segment): segment is Segment => segment !== undefined)
					.map(segment => ({
						departure: { iata: segment.departure.airport.iata },
						arrival: { iata: segment.arrival.airport.iata }
					})),
				allSegments: selectedService.isAllSegments,
				service: baggageMap.get(selectedService.serviceIds[0]),
				selectedServices: selectedService.selectedServices
			});
		}
	}

	return [...selectedServicesMap.values()];
};

export const isSelected = (baggage: Baggage, selectedServices: SelectedAdditionalBaggage[]): boolean => {
	return calcCount(baggage, selectedServices) > 0;
};

export const calcCount = (baggage: Baggage, selectedServices: SelectedAdditionalBaggage[]): number => {
	return selectedServices.filter(el => el.serviceId === baggage.id).reduce((acc, el) => acc + el.count, 0);
};

export const detectDirection = (
	baggage: Baggage,
	selectedServices: SelectedAdditionalBaggage[] | SelectedUpgrade[],
	order: CheckoutOrderFragment
): BaggageDirection => {
	const firstSegment = order.flight.segments[0];
	const lastSegment = order.flight.segments[order.flight.segments.length - 1];

	const allDirections = order?.travellers.map(traveller => {
		const services = selectedServices.filter(el => traveller.id === el.passengerId);

		if (services.length === 0) {
			return null;
		}

		return detectDirectionForTraveller(baggage, services, firstSegment, lastSegment);
	});

	const existRoundTrip = allDirections.some(direction => direction === BaggageDirection.RoundTrip);
	const existThere = allDirections.some(direction => direction === BaggageDirection.There);
	const existReturn = allDirections.some(direction => direction === BaggageDirection.Return);

	if (existRoundTrip) {
		return BaggageDirection.RoundTrip;
	} else if (existThere && existReturn) {
		return BaggageDirection.RoundTrip;
	} else if (existThere) {
		return BaggageDirection.There;
	} else if (existReturn) {
		return BaggageDirection.Return;
	}

	return null;
};

export const detectDirectionForTraveller = (
	baggage: Baggage,
	selectedServices: SelectedAdditionalBaggage[] | SelectedUpgrade[],
	firstSegment: SegmentWithTransferFragment,
	lastSegment: SegmentWithTransferFragment
): BaggageDirection => {
	const segmentIds: string[] = [];

	selectedServices
		.filter(service => baggage.id === service.serviceId)
		.forEach(el =>
			el.segmentIds.forEach(segmentId => {
				segmentIds.push(segmentId);
			})
		);

	if (segmentIds.length === 0) {
		return null;
	}

	const thereDirection = segmentIds.includes(firstSegment.segment.id);
	let returnDirection = false;

	if (firstSegment.segment.id !== lastSegment.segment.id) {
		returnDirection = segmentIds.includes(lastSegment.segment.id);
	}

	if (thereDirection && returnDirection) {
		return BaggageDirection.RoundTrip;
	} else if (thereDirection) {
		return BaggageDirection.There;
	} else if (returnDirection) {
		return BaggageDirection.Return;
	}

	return null;
};

export const factorySelectedServices = (selectedServices: SelectedBaggage[]): SelectedAdditionalBaggage[] => {
	const result: SelectedAdditionalBaggage[] = [];

	const addedKeys: Map<string, string> = new Map();

	for (const [passengerId, passengerSelectedServices] of Object.entries(selectedServices)) {
		for (const [serviceId, segments] of Object.entries(passengerSelectedServices)) {
			for (const [segmentId, selectedService] of Object.entries(segments)) {
				const segmentIds = selectedServices[passengerId][serviceId][segmentId]?.segmentIds;
				const key = `${passengerId}-${serviceId}-${segmentIds.join('-')}`;

				if (!addedKeys.has(key)) {
					result.push({
						passengerId,
						segmentIds: selectedServices[passengerId][serviceId][segmentId]?.segmentIds,
						serviceId,
						count: selectedService.count
					});

					addedKeys.set(key, key);
				}
			}
		}
	}

	return result;
};
